import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { searchWikiContent, WikiSearchResult } from "../../services/wiki.service";

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Wraps matched substring in <strong> without mutating surrounding text. */
function Highlight({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <strong>{text.slice(idx, idx + query.length)}</strong>
      {text.slice(idx + query.length)}
    </>
  );
}

const WikiSearchDialog: React.FC<Props> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery]       = useState("");
  const [results, setResults]   = useState<WikiSearchResult[]>([]);
  const [loading, setLoading]   = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setQuery("");
      setResults([]);
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced full-text search
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }

    setLoading(true);
    const timer = setTimeout(() => {
      searchWikiContent(q)
        .then((data) => { setResults(data || []); setActiveIdx(0); })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);

    return () => { clearTimeout(timer); setLoading(false); };
  }, [query]);

  const navigate_ = useCallback(
    (id: string) => { navigate(`/notes/${id}`); onClose(); },
    [navigate, onClose],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      const item = results[activeIdx];
      if (item) navigate_(item._id);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  const q = query.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { mt: "10vh", verticalAlign: "top", borderRadius: 2 } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <TextField
          inputRef={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search notes…"
          fullWidth
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {loading
                  ? <CircularProgress size={16} />
                  : <SearchIcon sx={{ color: "text.secondary" }} />}
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: "8px 8px 0 0", "& fieldset": { border: "none" } },
            "& .MuiInputBase-input": { fontSize: 15, py: 1.75 },
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        />

        <Box sx={{ maxHeight: 440, overflowY: "auto", py: 0.5 }}>
          {q.length < 2 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
              Type at least 2 characters to search
            </Typography>
          )}
          {q.length >= 2 && !loading && results.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
              No notes found
            </Typography>
          )}
          {results.map((item, idx) => {
            const isActive = idx === activeIdx;
            return (
              <Box
                key={item._id}
                onClick={() => navigate_(item._id)}
                onMouseEnter={() => setActiveIdx(idx)}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  cursor: "pointer",
                  bgcolor: isActive ? "action.selected" : "transparent",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <ArticleOutlinedIcon sx={{ fontSize: 16, color: "primary.main", flexShrink: 0, mt: "2px" }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={isActive ? 600 : 500}
                    sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    <Highlight text={item.title} query={q} />
                  </Typography>
                  {item.snippet && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.25, lineHeight: 1.5 }}
                    >
                      <Highlight text={item.snippet} query={q} />
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ px: 2, py: 0.75, borderTop: "1px solid", borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">
            ↑↓ navigate · Enter to open · Esc to close
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WikiSearchDialog;
